import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, verifyOtp, resetPassword } from '../lib/api';
import './ForgotPasswordPage.css';

/* ── Icons ── */
function ArrowLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <polyline points="2,4 12,13 22,4"/>
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );
}

/* ── Password strength helper ── */
function getStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)           score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: 'Too short', color: '#e74c3c' },
    { label: 'Weak',      color: '#e67e22' },
    { label: 'Fair',      color: '#f1c40f' },
    { label: 'Good',      color: '#2ecc71' },
    { label: 'Strong',    color: 'var(--forest)' },
  ];
  return { score, ...map[score] };
}

// STEP 1 — enter email
function StepEmail({ onNext }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      onNext(email.trim());
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fp-icon"><MailIcon /></div>
      <h1 className="fp-title">Forgot your password?</h1>
      <p className="fp-subtitle">
        No worries! Enter your email and we'll send you a 6-digit reset code.
      </p>

      {error && <div className="fp-alert fp-alert--error">{error}</div>}

      <form className="fp-form" onSubmit={handleSubmit} noValidate>
        <label className="fp-label">
          Email address
          <input
            className="fp-input"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </label>

        <button className="fp-btn" type="submit" disabled={loading}>
          {loading ? <span className="fp-spinner" /> : null}
          {loading ? 'Sending code…' : 'Send reset code'}
        </button>
      </form>
    </>
  );
}

// STEP 2 — enter OTP
function StepOtp({ email, onNext, onBack }) {
  const [otp,     setOtp]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(val);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) { setError('Please enter the full 6-digit code.'); return; }
    setLoading(true);
    try {
      const { resetToken } = await verifyOtp(email, otp);
      onNext(resetToken);
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await forgotPassword(email);
      setResendCountdown(60);
      setOtp('');
    } catch (err) {
      setError(err.message || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="fp-icon"><ShieldIcon /></div>
      <h1 className="fp-title">Enter your code</h1>
      <p className="fp-subtitle">We sent a 6-digit code to:</p>
      <div className="fp-email-badge">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/>
        </svg>
        {email}
      </div>

      {error && <div className="fp-alert fp-alert--error">{error}</div>}

      <form className="fp-form" onSubmit={handleSubmit} noValidate>
        <label className="fp-label">
          6-digit code
          <input
            ref={inputRef}
            className="fp-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={otp}
            onChange={handleOtpChange}
            maxLength={6}
          />
        </label>

        <button className="fp-btn" type="submit" disabled={loading || otp.length !== 6}>
          {loading ? <span className="fp-spinner" /> : null}
          {loading ? 'Verifying…' : 'Verify code'}
        </button>

        <div className="fp-resend-row">
          Didn't receive it?{' '}
          <button
            type="button"
            className="fp-resend-btn"
            onClick={handleResend}
            disabled={resendCountdown > 0 || resending}
          >
            {resending
              ? 'Resending…'
              : resendCountdown > 0
              ? `Resend in ${resendCountdown}s`
              : 'Resend code'}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <ArrowLeft /> Wrong email?
        </button>
      </div>
    </>
  );
}

// STEP 3 — new password
function StepNewPassword({ resetToken, onSuccess }) {
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [showConf,  setShowConf]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const strength = getStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password)               { setError('Please enter a new password.'); return; }
    if (password.length < 8)     { setError('Password must be at least 8 characters.'); return; }
    if (strength.score < 2)      { setError('Please choose a stronger password.'); return; }
    if (password !== confirm)    { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await resetPassword(resetToken, password);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please start over.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fp-icon"><LockIcon /></div>
      <h1 className="fp-title">Set new password</h1>
      <p className="fp-subtitle">Choose a strong password you haven't used before.</p>

      {error && <div className="fp-alert fp-alert--error">{error}</div>}

      <form className="fp-form" onSubmit={handleSubmit} noValidate>
        <label className="fp-label">
          New password
          <div className="fp-pw-wrap">
            <input
              className="fp-input"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button type="button" className="fp-pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
              <EyeIcon open={showPw} />
            </button>
          </div>
          {password && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <div style={{ flex: 1, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${(strength.score / 4) * 100}%`, height: '100%', background: strength.color, borderRadius: 2, transition: 'width 0.3s, background 0.3s' }} />
              </div>
              <span style={{ fontSize: 12, color: strength.color, fontWeight: 600, minWidth: 48 }}>{strength.label}</span>
            </div>
          )}
        </label>

        <label className="fp-label">
          Confirm password
          <div className="fp-pw-wrap">
            <input
              className="fp-input"
              type={showConf ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <button type="button" className="fp-pw-toggle" onClick={() => setShowConf(v => !v)} tabIndex={-1}>
              <EyeIcon open={showConf} />
            </button>
          </div>
        </label>

        <button className="fp-btn" type="submit" disabled={loading}>
          {loading ? <span className="fp-spinner" /> : null}
          {loading ? 'Saving…' : 'Reset password'}
        </button>
      </form>
    </>
  );
}

// STEP 4 — success
function StepSuccess() {
  return (
    <div className="fp-success">
      <div className="fp-success-icon"><CheckCircleIcon /></div>
      <h2 className="fp-success-title">Password updated!</h2>
      <p className="fp-success-sub">Your password has been reset successfully. You can now log in with your new password.</p>
      <Link to="/account" className="fp-btn" style={{ textDecoration: 'none', display: 'flex' }}>
        Go to login
      </Link>
    </div>
  );
}

/* ── Main page ── */
export default function ForgotPasswordPage() {
  const [step,       setStep]       = useState(1); // 1 | 2 | 3 | 4
  const [email,      setEmail]      = useState('');
  const [resetToken, setResetToken] = useState('');
  const navigate = useNavigate();

  const stepLabels = ['Email', 'Verify', 'Password', 'Done'];

  return (
    <div className="fp-page">
      <div className="fp-panel">
        <div className="fp-wrap">
          {/* Back to login */}
          {step < 4 && (
            <Link to="/account" className="fp-back">
              <ArrowLeft /> Back to login
            </Link>
          )}

          {/* Step progress dots */}
          <div className="fp-steps" aria-label="Progress">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`fp-step-dot${s === step ? ' fp-step-dot--active' : s < step ? ' fp-step-dot--done' : ''}`}
                title={stepLabels[s - 1]}
              />
            ))}
          </div>

          {step === 1 && (
            <StepEmail
              onNext={(e) => { setEmail(e); setStep(2); }}
            />
          )}

          {step === 2 && (
            <StepOtp
              email={email}
              onNext={(token) => { setResetToken(token); setStep(3); }}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && (
            <StepNewPassword
              resetToken={resetToken}
              onSuccess={() => setStep(4)}
            />
          )}

          {step === 4 && <StepSuccess />}
        </div>
      </div>
    </div>
  );
}
